// src/clients/cart/services/offer-validation-service.ts
// ✅ VERSIÓN LIMPIA: Sin modales informativos molestos, detección mejorada

import { cartApiService } from './cart-api-service';
import type { ProductoCarrito } from './cart-service-hybrid';

/* ========================= Tipos ========================= */

export interface OfferValidationResult {
  tiene_oferta: boolean;
  cumple_oferta: boolean;
  cantidad_minima: number;
  precio_normal: number;
  precio_oferta: number;
  ahorro_total: number;
  mensaje_usuario?: string;
  es_multiplo: boolean;
  cantidad_multiplo_siguiente?: number;
}

export interface CambiarCantidadValidation {
  puede_cambiar: boolean;
  pierde_promocion: boolean;
  gana_promocion: boolean;
  cantidad_para_promo?: number;
  mensaje_confirmacion?: string;
  accion_sugerida?: 'reducir_sin_promo' | 'aumentar_para_promo' | 'proceder_normal';
}

/* ========================= Servicio ========================= */
class OfferValidationService {
  /**
   * 🔍 Valida si un producto tiene ofertas de cantidad disponibles
   * Usa el endpoint /api/v1/carrito/validar-oferta
   */
  async validarOfertasProducto(
    cod_drog: string,
    cantidad: number
  ): Promise<OfferValidationResult> {
    try {
      const response = await cartApiService.validarOfertas({ cod_drog, cantidad });

      console.log('📦 [validarOfertasProducto] Response:', {
        cod_drog,
        cantidad,
        success: response.success,
        data: response.data
      });

      if (!response.success || !response.data) {
        return this.crearResultadoSinOferta();
      }

      const descuentos = response.data.descuentos_ofertas;
      const recomendaciones = Array.isArray(response.data.recomendaciones)
        ? response.data.recomendaciones
        : [];

      // Datos base desde descuentos
      let tiene_oferta = !!descuentos?.tiene_oferta_cantidad;
      let cumple_oferta = !!descuentos?.cumple_oferta;
      let cantidad_minima = Number(descuentos?.cantidad_minima_oferta || 0);
      let precio_normal = parseFloat(String(descuentos?.precio_normal || 0));
      let precio_oferta = parseFloat(
        String(
          descuentos?.precio_oferta ??
          (descuentos as any)?.precio_final ??
          descuentos?.precio_normal ??
          0
        )
      );
      let ahorro_total = parseFloat(String(descuentos?.ahorro_total || 0));

      // Complementar con recomendaciones
      const rec = recomendaciones.find((r: any) => String(r?.tipo) === 'oferta_disponible');
      if (rec) {
        tiene_oferta = true;
        if (!cantidad_minima) cantidad_minima = Number(rec.cantidad_minima || 0);
        if (!precio_normal) precio_normal = parseFloat(String(rec.precio_normal || 0));
        if (!precio_oferta) precio_oferta = parseFloat(String(rec.precio_oferta || 0));
        if (!cumple_oferta && cantidad_minima > 0) {
          cumple_oferta = Number(cantidad) >= Number(cantidad_minima);
        }
      }

      // ✅ VALIDAR MÚLTIPLOS: si cantidad_minima existe, validar que la cantidad sea múltiplo
      const esMultiplo = cantidad_minima <= 0 || cantidad % cantidad_minima === 0;
      const cantidadMultiploSiguiente = esMultiplo
        ? undefined
        : Math.ceil(cantidad / cantidad_minima) * cantidad_minima;

      const result: OfferValidationResult = {
        tiene_oferta,
        cumple_oferta: cumple_oferta && esMultiplo, // ✅ Solo cumple si es múltiplo
        cantidad_minima,
        precio_normal: isFinite(precio_normal) ? precio_normal : 0,
        precio_oferta: isFinite(precio_oferta) ? precio_oferta : 0,
        ahorro_total: isFinite(ahorro_total) ? ahorro_total : 0,
        es_multiplo: esMultiplo,
        cantidad_multiplo_siguiente: cantidadMultiploSiguiente,
      };

      console.log('✅ [validarOfertasProducto] Result:', result);

      return result;
    } catch (error) {
      console.error('❌ [validarOfertasProducto] Error:', error);
      return this.crearResultadoSinOferta();
    }
  }

  /**
   * 🎯 Valida si un cambio de cantidad afecta una promoción
   * ✅ SIMPLIFICADO: Solo detecta casos críticos
   */
  async validarCambioCantidadConCantidadActual(
    producto: ProductoCarrito,
    cantidadActual: number,
    cantidadNueva: number
  ): Promise<CambiarCantidadValidation> {
    const estaReduciendo = cantidadNueva < cantidadActual;
    const estaAumentando = cantidadNueva > cantidadActual;

    // Determinar si el producto tiene oferta aplicada usando el estado local del producto
    // (evita una segunda llamada a la API para validar la cantidad actual)
    const p = producto as any;
    // tipo_solicitante="C" significa que el backend ya lo tiene como producto común (sin oferta)
    // En ese caso no hay oferta que perder ni ganar por cambio de cantidad
    const tipoSolicitante = String(p.tipo_solicitante || '').toUpperCase();
    // tipo_solicitante="O" → tiene oferta aplicada (puede perderla)
    // tipo_solicitante="C" → no tiene oferta aplicada (puede ganarla si sube cantidad)
    const tieneOfertaAplicada = tipoSolicitante === 'O';

    // Si no tiene oferta y no está aumentando, no hay nada que evaluar
    if (!tieneOfertaAplicada && !estaAumentando) {
      return { puede_cambiar: true, pierde_promocion: false, gana_promocion: false, accion_sugerida: 'proceder_normal' };
    }
    // Si tiene oferta y no está reduciendo, no hay nada que evaluar
    if (tieneOfertaAplicada && !estaReduciendo) {
      return { puede_cambiar: true, pierde_promocion: false, gana_promocion: false, accion_sugerida: 'proceder_normal' };
    }

    // Una sola llamada: solo necesitamos saber si la cantidad NUEVA cumple oferta
    const validacionNueva = await this.validarOfertasProducto(producto.cod_drog, cantidadNueva);

    // 🔴 CASO 1: tiene oferta ("O"), reduce, y la nueva cantidad no cumple el mínimo
    if (
      tieneOfertaAplicada &&
      validacionNueva.tiene_oferta &&
      (!validacionNueva.cumple_oferta || !validacionNueva.es_multiplo)
    ) {
      return {
        puede_cambiar: true,
        pierde_promocion: true,
        gana_promocion: false,
        cantidad_para_promo: validacionNueva.cantidad_minima,
        mensaje_confirmacion: this.construirMensajePierdePromo(producto, cantidadNueva, validacionNueva),
        accion_sugerida: 'reducir_sin_promo',
      };
    }

    // 🟢 CASO 2: no tiene oferta ("C"), aumenta, y la nueva cantidad cumple el mínimo
    if (
      !tieneOfertaAplicada &&
      validacionNueva.tiene_oferta &&
      validacionNueva.cumple_oferta &&
      validacionNueva.es_multiplo
    ) {
      return {
        puede_cambiar: true,
        pierde_promocion: false,
        gana_promocion: true,
        mensaje_confirmacion: this.construirMensajeGanaPromo(producto, cantidadNueva, validacionNueva),
        accion_sugerida: 'aumentar_para_promo',
      };
    }

    // 🔵 CASO 3: No afecta promoción
    return {
      puede_cambiar: true,
      pierde_promocion: false,
      gana_promocion: false,
      accion_sugerida: 'proceder_normal',
    };
  }

  /** 🧹 Limpia el estado (no-op sin cache) */
  clearCache() {
    console.log('🗑️ Cache de validaciones removido');
  }

  /* ======================== Helpers privados ======================== */

  /**
   * ✅ Detecta si un producto tiene oferta REALMENTE aplicada
   * Evita falsos positivos
   */
  private detectarOfertaAplicada(producto: ProductoCarrito): boolean {
    const p = producto as any;

    // Si marcamos que perdió la oferta, no hay vuelta atrás para esa línea
    if (p.__oferta_perdida === true) return false;

    // 1. Si está marcado explícitamente como Oferta o Promocional
    const tipoSolic = String(p.tipo_solicitante || '').toUpperCase();
    if (tipoSolic === 'O') return true;
    if (p.promocional === true) return true;

    // 2. Validación estricta por precio:
    // Solo consideramos que tiene oferta si el precio unitario actual coincide con el precio de oferta
    const precioOferta = Number(p.precio_oferta || 0);
    const precioUnitario = Number(p.precio_unitario || 0);

    // Debe tener un precio de oferta válido
    if (precioOferta > 0) {
      // Y ADEMÁS, el precio que está pagando el cliente debe ser ese precio de oferta
      if (Math.abs(precioUnitario - precioOferta) < 0.02) {
        return true;
      }
    }

    return false;
  }



  private crearResultadoSinOferta(): OfferValidationResult {
    return {
      tiene_oferta: false,
      cumple_oferta: false,
      cantidad_minima: 0,
      precio_normal: 0,
      precio_oferta: 0,
      ahorro_total: 0,
      es_multiplo: true,
    };
  }

  private construirMensajePierdePromo(
    producto: ProductoCarrito,
    cantidadNueva: number,
    validacion: OfferValidationResult
  ): string {
    return `Vas a perder la promo`;
  }

  private construirMensajeGanaPromo(
    producto: ProductoCarrito,
    cantidadNueva: number,
    validacion: OfferValidationResult
  ): string {
    return `¡Vas a ganar la promo!`;
  }
}

/* ========================= Singleton + Debug ========================= */

export const offerValidationService = new OfferValidationService();

// Debug en dev
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  (window as any).offerValidationDebug = {
    validar: (cod: string, cant: number) =>
      offerValidationService.validarOfertasProducto(cod, cant),
    clearCache: () => offerValidationService.clearCache(),
  };
}